import React, { useState } from 'react';
import axios from 'axios';
import { Bell, Send, Users, CheckCircle2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

interface NotificationForm {
  title: string;
  body: string;
  targetTier: string;
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl p-5 backdrop-blur-sm";
const INPUT = "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-all";
const BTN_PRIMARY = "inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

function NotificationsPage() {
  const [form, setForm] = useState<NotificationForm>({ title: '', body: '', targetTier: 'all' });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.body) { setError('Please fill in all fields'); return; }
    try {
      setSending(true); setError(null); setSuccess(false);
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API_URL}/api/admin/broadcast-notification`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(true);
      setForm({ title: '', body: '', targetTier: 'all' });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const templates = [
    { title: '🎉 New Feature Announcement', body: 'Check out our latest feature that will help you manage your budget better!', icon: '🚀' },
    { title: '⚠️ System Maintenance', body: 'We will be performing scheduled maintenance on [DATE]. Expected downtime: 2 hours.', icon: '🔧' },
    { title: '💡 Budget Tip of the Week', body: 'Try setting aside 10% of each paycheck for emergency savings!', icon: '💰' },
    { title: '🎁 Special Offer', body: 'Upgrade to Premium now and get 20% off your first 3 months!', icon: '🎁' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Push Notifications</h1>
        <p className="text-white/40 text-sm mt-1">Send notifications to all users or specific tiers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className={CARD}>
          <div className="flex items-center gap-2 mb-5">
            <Bell size={18} className="text-primary" />
            <h2 className="text-base font-semibold text-white">Send New Notification</h2>
          </div>

          {success && (
            <div className="flex items-center gap-2 bg-green/10 border border-green/20 text-green rounded-lg px-4 py-3 text-sm mb-4">
              <CheckCircle2 size={16} /> Notification sent successfully!
            </div>
          )}
          {error && (
            <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Notification Title *</label>
              <input type="text" className={INPUT} placeholder="e.g., New Feature Available!" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={50} required />
              <p className="text-white/30 text-xs mt-1">{form.title.length}/50 characters</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Notification Message *</label>
              <textarea className={INPUT} placeholder="Write your notification message here..." value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} maxLength={200} required />
              <p className="text-white/30 text-xs mt-1">{form.body.length}/200 characters</p>
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-white/50 mb-1.5">
                <Users size={13} /> Target Audience
              </label>
              <select className={INPUT} value={form.targetTier} onChange={(e) => setForm({ ...form, targetTier: e.target.value })}>
                <option value="all">All Users</option>
                <option value="free">Free Tier Only</option>
                <option value="standard">Standard Tier Only</option>
                <option value="premium">Premium Tier Only</option>
                <option value="paid">All Paid Users</option>
              </select>
            </div>

            <button type="submit" className={BTN_PRIMARY} disabled={sending}>
              {sending ? (
                <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> Sending...</>
              ) : (
                <><Send size={15} /> Send Notification</>
              )}
            </button>
          </form>
        </div>

        {/* Templates + Best Practices */}
        <div className="space-y-4">
          <div className={CARD}>
            <h3 className="text-sm font-semibold text-white mb-1">Quick Templates</h3>
            <p className="text-white/40 text-xs mb-4">Click a template to use it</p>
            <div className="space-y-2">
              {templates.map((t, i) => (
                <button key={i} onClick={() => setForm({ ...form, title: t.title, body: t.body })}
                  className="w-full text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.07] rounded-xl p-3 transition-all group">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{t.icon}</span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-white/80 group-hover:text-white transition-colors truncate">{t.title}</p>
                      <p className="text-white/40 text-xs mt-0.5 line-clamp-2">{t.body}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className={CARD}>
            <h3 className="text-sm font-semibold text-white mb-3">Best Practices</h3>
            <ul className="space-y-1.5 text-xs text-white/50">
              <li>✅ Keep titles under 50 characters</li>
              <li>✅ Make messages clear and actionable</li>
              <li>✅ Use emojis sparingly for visual appeal</li>
              <li>✅ Test with a small group first</li>
              <li>✅ Avoid sending too many notifications</li>
              <li className="text-red/70">✗ Don't use ALL CAPS</li>
              <li className="text-red/70">✗ Avoid sending late at night</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationsPage;
